import { DateRange, LQ, TimestampRange, UTC, guid, isNullOrUndefined } from 'rewire-common';
import { freeze, observable } from 'rewire-core';
import { ICell } from 'rewire-grid';
import { IActionMenuItem, ActionType } from 'rewire-ui';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import UndoIcon from '@material-ui/icons/UndoOutlined';
import LogIcon from '@material-ui/icons/AssignmentOutlined';
import { EventEmitter } from 'events';
import {
  DeepPartial,
  IJob,
  IShift,
  ITimecard,
  IOccupation,
  IEmployee,
  ITemplate,
  MessageInput,
  IUdf,
  ReasonCategory,
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import scheduleModel, { timecardIntersects } from './ScheduleModel';
import scheduleInfoPanelModel from './ScheduleInfoPanelModel';
import { getTemplateTimecardTemplates } from '../timecard/edit/EditTimecardModel';
import infoPanelShiftDetailsViewModel, { InfoPanelShiftDetailsViewModel } from './InfoPanelShiftDetailsViewModel';
import infoPanelShiftEmployeesViewModel, { InfoPanelShiftEmployeesViewModel } from './InfoPanelShiftEmployeesViewModel';
import infoPanelShiftResponseLogViewModel, { InfoPanelShiftResponseLogViewModel } from './InfoPanelShiftResponseLogViewModel';
import infoPanelShiftSchedulingViewModel, { InfoPanelShiftSchedulingViewModel } from './InfoPanelShiftSchedulingViewModel';
import infoPanelShiftTimeOffRequestViewModel, {
  InfoPanelShiftTimeOffRequestViewModel,
} from './InfoPanelShiftTimeOffRequestViewModel';
import auditLogDialogModel from '../audit/AuditLogDialogModel';
import { IAuditLogFilters } from '../audit/AuditLogListModel';
import { scheduleDisabledAuditLogColumns } from '../audit/AuditLogItemViewModel';
import commentDialogModel from './CommentDialogModel';
import { IDuplicateShiftData } from './DuplicateShiftDialogModel';
import duplicateMultipleShiftsDialogModel from './DuplicateMultipleShiftsDialogModel';
import { getMinMaxDates } from '../utility/DateUtils';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';

export interface IInfoPanelShiftViewModelChangeHandler {
  onCurrentInnerShiftChange: () => void;
}

type SelectionState = {
  employee?: IEmployee;
  occupation?: IOccupation;
  template?: ITemplate;
  udfs: IUdf[];
  reason: { id: string; name: string; code?: string; category: ReasonCategory };
  fields: Record<string, unknown>;
};

export type IShiftViewType = 'details' | 'employees' | 'scheduling' | 'timeOff' | 'response log';

export interface IShiftInfo {
  job?: IJob;
  template?: ITemplate;
  details: ITimecard[];
  defaultTimecard: ITimecard;
  hasChanges: boolean;
  isNew: boolean;
}

interface IInfoPanelShiftViewModelState {
  view: IShiftViewType;
  job?: IJob;
  shift?: IShift;
  innerShifts: IShiftInfo[];
  currentInnerShiftIdx: number;
  isLoading: boolean;
  isMultiShiftSelection: boolean;
}

export class InfoPanelShiftViewModel extends EventEmitter {
  state: IInfoPanelShiftViewModelState;
  noShiftOptionsActionMenuItems: IActionMenuItem[];

  constructor() {
    super();
    this.state = observable({
      view: 'details' as const,
      job: undefined,
      shift: undefined,
      isMultiShiftSelection: false,
      innerShifts: [],
      currentInnerShiftIdx: 0,
      isLoading: false,
    });
    this.noShiftOptionsActionMenuItems = [
      {
        name: 'resetToDefault',
        title: 'Reset to Default',
        icon: UndoIcon,
        onClick: () => this.resetShiftToDefault(),
        closeOnClick: true,
        disabled: () => this.viewOnly,
        visible: () => scheduleModel.viewMode === 'Job',
      },
      {
        name: 'addComment',
        title: 'Add Comment',
        icon: AddIcon,
        onClick: () => this.addComment(),
        closeOnClick: true,
        disabled: () => this.viewOnly,
      },
      { name: 'auditLog', title: 'View Audit Log', icon: LogIcon, onClick: () => this.viewAuditLog(), closeOnClick: true },
    ];
  }
  reset() {
    freeze(() => {
      this.state.isLoading = false;
      this.state.job = undefined;
      this.state.shift = undefined;
      this.state.innerShifts.length = 0;
    });
    this.currentViewModel.reset();
  }

  getOrcreateSelectionState(selectionStateMap: Map<number, SelectionState>, shiftNbr: number): SelectionState {
    const selectionState = selectionStateMap.get(shiftNbr);
    if (selectionState) {
      return selectionState;
    }
    const newSelectionState: SelectionState = {
      employee: undefined,
      occupation: undefined,
      template: undefined,
      reason: { id: 'none', name: '«Reason»', code: 'sr', category: 'worked' as ReasonCategory },
      fields: {},
      udfs: udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType('timecards') ?? [],
    } as SelectionState;
    selectionStateMap.set(shiftNbr, newSelectionState);
    return newSelectionState;
  }

  getMultiSelectShift(shifts: IShift[]): { shift: IShift | undefined; isMultiShiftSelection: boolean } {
    const selectionStateByShift = new Map<number, SelectionState>();

    const setEmployee = (selectionState: SelectionState, emp: IEmployee | undefined) => {
      if (selectionState.employee?.id !== 'none') {
        if (selectionState.employee && selectionState.employee?.id !== emp?.id) {
          selectionState.employee = { id: 'none', name: '«Employees»' };
        } else {
          selectionState.employee = emp;
        }
      }
    };
    const setOccupation = (selectionState: SelectionState, occ: IOccupation | undefined) => {
      if (selectionState.occupation?.id !== 'none') {
        if (selectionState.occupation && occ && selectionState.occupation.id !== occ.id) {
          selectionState.occupation = { id: 'none', name: '«Occupations»' };
        } else {
          selectionState.occupation = occ;
        }
      }
    };
    const setTemplate = (selectionState: SelectionState, temp: ITemplate | undefined) => {
      if (selectionState.template?.id !== 'none') {
        if (selectionState.template && temp && selectionState.template.id !== temp.id) {
          selectionState.template = { id: 'none', name: '«Templates»', template: selectionState.template.template, locked: false };
          for (const t of selectionState.template.template ?? ([] as any[])) {
            if (t.start && t.end) {
              t.start = undefined;
              t.end = undefined;
            }
          }
        } else {
          selectionState.template = temp;
        }
      }
    };

    if (!shifts || shifts.length === 0) {
      for (const jobShifts of scheduleModel.getSelectedCells()) {
        const [job, _dt, _shift] = jobShifts;
        const selectionState = this.getOrcreateSelectionState(selectionStateByShift, 0);
        setEmployee(selectionState, job?.employee);
        setOccupation(selectionState, job?.occupation);
      }
      return undefined as any;
    }

    const maxShiftNbrByDate = new Map<string, number>();
    for (const shift of shifts) {
      if (!shift || !shift.details) {
        continue;
      }

      for (const detail of shift.details) {
        const key = `${detail.job?.id ?? ''}:${detail.earnedDate.utc}`;
        maxShiftNbrByDate.set(key, Math.max(maxShiftNbrByDate.get(key) ?? 0, detail.shift));
      }
    }

    let isMultiShiftSelection = false;
    let prev = -1;
    for (const [_key, maxShiftNbr] of maxShiftNbrByDate) {
      if (prev === -1) {
        prev = maxShiftNbr;
        continue;
      }
      if (prev !== maxShiftNbr) {
        isMultiShiftSelection = true;
        break;
      }
      prev = maxShiftNbr;
    }

    for (const shift of shifts) {
      if (!shift || !shift.details) {
        continue;
      }

      for (const detail of shift.details) {
        const selectionState = this.getOrcreateSelectionState(selectionStateByShift, isMultiShiftSelection ? 0 : detail.shift);
        if (detail.template) {
          setTemplate(selectionState, detail.template);
        }

        if (['worked', 'break'].includes(detail.reason?.category || '')) {
          setEmployee(selectionState, detail.employee ?? { id: 'empty', name: '«Empty»' });
        }

        if (detail.occupation) {
          setOccupation(selectionState, detail.occupation);
        }
        if (!detail.fields) {
          continue;
        }

        for (const udf of selectionState.udfs) {
          const detailValue = (detail as any).fields[udf.name as string];
          if (!detailValue) {
            continue;
          }

          const value = selectionState.fields[udf.name as string] as { id: string; name: string } | undefined;
          if (udf.type === 'reference' && typeof detailValue === 'object') {
            if (value?.id !== 'none') {
              if (value && value.id !== detailValue.id) {
                selectionState.fields[udf.name as string] = { id: 'none', name: `«${udf.displayName}»` };
              } else {
                selectionState.fields[udf.name as string] = detailValue;
              }
            }
          } else {
            if (value !== undefined) {
              if (value !== detailValue) {
              } else {
                selectionState.fields[udf.name as string] = detailValue;
              }
            }
          }
        }
      }
    }

    const shift = shifts.find((s) => s?.details?.length) ?? undefined;
    if (!shift) {
      return { shift, isMultiShiftSelection };
    }
    const details = [] as ITimecard[];
    const earnedDate = shift.earnedDate;
    const detailsWithNoTimes = new Map<number, ITimecard>();
    for (const detail of shift.details ?? []) {
      if (detail.reason?.category === 'absence') {
        continue;
      }
      const d = JSON.parse(JSON.stringify(detail)) as ITimecard;
      const selectionState = selectionStateByShift.get(isMultiShiftSelection ? 0 : detail.shift);

      d.employee = selectionState?.employee;
      d.occupation = selectionState?.occupation;
      d.template = selectionState?.template;
      if (d.reason?.category === 'worked') {
        d.reason = selectionState?.reason;
      }
      d.fields = selectionState?.fields;
      d.earnedDate = new UTC(d.earnedDate);
      if (selectionState?.template?.id === 'none') {
        if (!d.units) {
          continue;
        }
        const detailNoTimes = detailsWithNoTimes.get(detail.shift);
        if (detailNoTimes) {
          detailNoTimes.units! += d.units!;
          continue;
        }
        d.effective = undefined;
        detailsWithNoTimes.set(detail.shift, d);
        // d.units = units;
      } else {
        if (d.effective) {
          d.effective = new TimestampRange(d.effective);
        }
      }
      details.push(d);
    }

    return { shift: { earnedDate, details, template: details?.[0]?.template ?? shift.template } as IShift, isMultiShiftSelection };
  }

  async load(shift?: null | IShift | IShift[]) {
    try {
      this.reset();
      this.state.isLoading = true;
      let isMultiShiftSelection = false;
      if (Array.isArray(shift)) {
        const result = this.getMultiSelectShift(shift);
        shift = result.shift;
        isMultiShiftSelection = result.isMultiShiftSelection;
      }
      const job = scheduleModel.getJobFromCell(this.cell!);
      freeze(() => {
        this.state.shift = shift as IShift | undefined;
        this.state.job = job;
        this.state.isMultiShiftSelection = isMultiShiftSelection;
        if (isMultiShiftSelection) {
          this.state.currentInnerShiftIdx = 0;
        }
        if (scheduleModel.viewMode === 'Employee' && this.view === 'scheduling') {
          this.state.view = 'details';
        }
      });
      await this.loadInnerShifts();
      // if multi shift selection, we only load details view for one shift which we will apply to all selected shifts.
      if (isMultiShiftSelection) {
        this.innerShifts.length = 1;
      }
      await this.loadView();
    } finally {
      this.state.isLoading = false;
    }
  }

  async loadInnerShifts() {
    let details = this.shift?.details?.slice() ?? [];
    this.state.innerShifts.length = 0;

    if (details.length === 0) {
      this.addInnerShift(0, [], this.employee, this.job, this.template, true);
    } else {
      const detailsIds = details.map((detail) => detail.id) ?? [];
      const enhancedDetails = await this.getEnhancedDetails(detailsIds);
      const sortedEnhancedDetails = details
        .map((detail) => {
          const newDetail = enhancedDetails.find((d) => d.id === detail.id);
          if (!newDetail) {
            return null;
          }
          if (['worked', 'break'].includes(detail.reason?.category || '')) {
            newDetail.employee = detail.employee;
            newDetail.occupation = detail.occupation;
            newDetail.reason = detail.reason;
            newDetail.template = detail.template;
            newDetail.fields = detail.fields;
            newDetail.effective = detail.effective;
            newDetail.units = detail.units;
            if (detail?.employee?.id === 'empty') {
              newDetail.employee = undefined;
            }
          }
          return newDetail;
        })
        .filter((d) => !!d) as ITimecard[];
      details = observable(sortedEnhancedDetails);

      this.sortAndAdjustDetailsToHaveContiguousShifts(details);
      let idx = 0;
      let curr = 0;
      while (idx < details.length) {
        const detail = details[idx];
        if (!detail || !detail.reason?.category || !['worked', 'absence', 'break'].includes(detail.reason!.category)) {
          // skip premiums etc...
          idx++;
          continue;
        }

        if (scheduleModel.viewMode === 'Job') {
          curr = Number.isNaN(detail.shift) ? 0 : detail.shift;
        }
        let shift = this.innerShifts[curr];
        if (!shift) {
          const template = this.shift?.template ?? detail.template;
          if (template && template.id !== 'none' && !template.template) {
            template.template = (await getTemplateTimecardTemplates(template.id))!;
          }
          detail.template = template;
          shift = this.addInnerShift(curr, [], detail.employee, detail.job, template);
        }

        if (
          timecardIntersects(shift.details, detail) ||
          (scheduleModel.viewMode === 'Job' && shift.template?.id !== detail.template?.id)
        ) {
          // shift along and try again
          if (scheduleModel.viewMode === 'Job') {
            detail.shift = curr + 1;
          } else {
            curr++;
          }
          continue;
        }

        shift.details.push(detail);
        idx++;
        curr = 0;
      }
    }
  }

  private getEnhancedDetails = async (timecardIds: string[]) => {
    const returnSchema = `
      id
      earnedDate
      effective
      accountCode
      code
      units
      factor
      rate
      amount
      shift
      createdBy
      createdDate
      template {
        id
        name
        template {
          start
          end
          units
          rate
          factor
          amount
          accountCode
          reason {
            id
            name
            category
          }
          overtimeReason {
            id
            name
          }
        }
      }
      job {
        id
        effective
        cycleOffset
        fields
        position
        generator {
          id
          name
          cycle {
            id
            name
          }
        }
        group {
          id
          name
          area {
            id
            name
          }
        }
        occupation {
          id
          name
        }
        employee {
          id
          name
        }
      }
      group {
        id
        name
        fields
        area {
          id
          name
        }
      }
      tags
      fields
      occupation {
        id
        name
      }
      employee {
        id
        name
        code
        tags
        email
        archived
        fields
        phone {
          home
          mobile
          work
          fax
          workFax
        }
      }
      reason {
        id
        name
        category
      }
      overtimeReason {
        id
        name
      }
    `;

    const enhancedDetails = await DataQueries.getTimecardsByIds(returnSchema, timecardIds);
    return (enhancedDetails?.filter((detail) => !!detail) ?? []) as ITimecard[];
  };

  private sortFn(a: ITimecard, b: ITimecard) {
    const r = (Number.isNaN(a.shift) ? 0 : a.shift) - (Number.isNaN(b.shift) ? 0 : b.shift);
    if (r !== 0) return r;
    const t = (a.effective?.start?.utc ?? Number.POSITIVE_INFINITY) - (b.effective?.start?.utc ?? Number.POSITIVE_INFINITY);
    if (t !== 0) return t;
    const employeeName1 = a.employee?.name ?? '';
    const employeeName2 = b.employee?.name ?? '';
    const e = employeeName1.localeCompare(employeeName2);
    if (e !== 0) return e;
    const employeeId1 = a.employee?.id ?? '';
    const employeeId2 = b.employee?.id ?? '';
    return employeeId1.localeCompare(employeeId2);
  }

  sortAndAdjustDetailsToHaveContiguousShifts(details: ITimecard[]) {
    details.sort(this.sortFn);
    let prevShift: number | undefined;
    let adjustedShift = 0;
    for (const detail of details) {
      const currShift = detail.shift ?? 0;
      if (!isNullOrUndefined(prevShift) && currShift > prevShift) {
        adjustedShift++;
      }
      prevShift = currShift;
      detail.shift = adjustedShift;
    }
  }

  loadView = async () => {
    const viewModel = this.currentViewModel;
    if (viewModel instanceof InfoPanelShiftSchedulingViewModel) {
      await viewModel.load(true);
    } else {
      await viewModel.load();
    }
  };

  getViewModel(
    view: IShiftViewType,
  ):
    | InfoPanelShiftDetailsViewModel
    | InfoPanelShiftEmployeesViewModel
    | InfoPanelShiftSchedulingViewModel
    | InfoPanelShiftResponseLogViewModel
    | InfoPanelShiftTimeOffRequestViewModel {
    if (view === 'details') {
      return infoPanelShiftDetailsViewModel;
    }
    if (view === 'employees') {
      return infoPanelShiftEmployeesViewModel;
    }
    if (view === 'scheduling') {
      return infoPanelShiftSchedulingViewModel;
    }
    if (view === 'response log') {
      return infoPanelShiftResponseLogViewModel;
    }
    return infoPanelShiftTimeOffRequestViewModel;
  }

  get currentViewModel(): { isDisabled?: boolean; actions?: any } & (
    | InfoPanelShiftDetailsViewModel
    | InfoPanelShiftEmployeesViewModel
    | InfoPanelShiftSchedulingViewModel
    | InfoPanelShiftTimeOffRequestViewModel
    | InfoPanelShiftResponseLogViewModel
  ) {
    const view = this.isNew ? 'details' : this.view;
    return this.getViewModel(view);
  }

  get isNew(): boolean {
    return !this.shift;
  }

  get isNewInnerShift(): boolean {
    return this.innerShifts[this.currentInnerShiftIdx]?.isNew ?? false;
  }

  get innerShifts(): IShiftInfo[] {
    return this.state.innerShifts;
  }

  get currentInnerShiftIdx(): number {
    return this.innerShifts.length <= this.state.currentInnerShiftIdx
      ? this.innerShifts.length - 1
      : this.state.currentInnerShiftIdx;
  }

  set currentInnerShiftIdx(value: number) {
    const newCurrentInnerShiftIdx = Math.max(0, Math.min(value, this.innerShifts.length - 1));
    this.state.currentInnerShiftIdx = newCurrentInnerShiftIdx;
    this.currentViewModel.onCurrentInnerShiftChange();
  }

  get currentInnerShift(): IShiftInfo | undefined {
    return this.innerShifts[this.currentInnerShiftIdx];
  }

  get cell(): ICell | undefined {
    return scheduleInfoPanelModel.selectedCell;
  }

  get shift(): IShift | undefined {
    return this.state.shift;
  }

  get occupation(): IOccupation | undefined {
    return this.state.job?.occupation;
  }

  get employee(): IEmployee | undefined {
    return this.state.job?.employee;
  }

  get earnedDate(): UTC {
    return this.shift?.earnedDate ?? scheduleModel.getShiftCellEarnedDate(this.cell!);
  }

  get details(): DeepPartial<ITimecard>[] | undefined {
    return this.shift?.details;
  }

  get template(): ITemplate | undefined {
    return this.shift?.template;
  }

  get view(): IShiftViewType {
    return this.state.view;
  }

  get viewOnly(): boolean {
    return scheduleInfoPanelModel.viewOnly;
  }

  get job(): IJob | undefined {
    return this.state.job;
  }

  get jobId(): string | undefined {
    return this.state.job?.id;
  }

  get jobGroupId(): string | undefined {
    return this.state.job?.group?.id;
  }

  get isLoading(): boolean {
    return this.state.isLoading;
  }

  get jobAutoCreateVacancyForAbsenceReasons(): boolean {
    return !!this.state.job?.fields?.['_autoCreateVacancyForAbsenceReasons:b'];
  }

  get editing(): boolean {
    return !!scheduleModel.getShifts().next().value?.details?.length;
    // return !!this.details;
  }

  get isDisabled(): boolean {
    return this.currentViewModel['isDisabled'] || false;
  }

  get optionsActionMenuItems(): IActionMenuItem[] {
    if (this.isNew) {
      return this.noShiftOptionsActionMenuItems;
    }
    return this.currentViewModel.optionsActionMenuItems;
  }

  get actions(): { [index: string]: ActionType } | undefined {
    return this.currentViewModel['actions'];
  }

  get navigateForwardTitle() {
    return this.getText(this.currentInnerShiftIdx + 1);
  }

  get navigateBackTitle() {
    return this.getText(this.currentInnerShiftIdx - 1);
  }

  get shiftHasAbsenceReason(): boolean {
    return LQ(scheduleModel.getShifts())
      .flatMap((s) => s.details ?? [])
      .contains((s) => s.reason?.category === 'absence');
  }

  hasChanges(): boolean {
    return this.innerShifts.some((t) => t.hasChanges);
  }

  canNavigateForward(): boolean {
    return this.currentInnerShiftIdx < this.innerShifts.length - 1;
  }

  canNavigateBack(): boolean {
    return this.currentInnerShiftIdx > 0 && this.innerShifts.length > 1;
  }

  navigateForward(): boolean {
    if (this.canNavigateForward()) {
      this.currentInnerShiftIdx++;
      return true;
    }
    return false;
  }

  navigateBack(): boolean {
    if (this.canNavigateBack()) {
      this.currentInnerShiftIdx--;
      return true;
    }
    return false;
  }

  getText(index: number) {
    if (index < 0 || index >= this.innerShifts.length) return undefined;
    const shift = this.innerShifts[index];
    const shiftDetail = shift.details.find((sd) => !!sd.employee?.name && sd.reason?.category);
    if (!shiftDetail) return '«empty»';
    const templateText = shift.template?.name ?? '«no template»';
    if (scheduleModel.viewMode === 'Job') {
      const employeeText = shiftDetail.employee!.name;
      return `${templateText} - ${employeeText}`;
    }
    return `${templateText}`;
  }

  isOutsideJobEffectiveRange(): boolean {
    return !this.job?.effective.inRange(this.earnedDate);
  }

  addInnerShift(
    idx: number,
    details: ITimecard[],
    employee: IEmployee | undefined,
    job: IJob | undefined,
    template: ITemplate | undefined,
    isNew = false,
  ): IShiftInfo {
    if (!job) {
      job = this.job;
    }
    const earnedDate = this.earnedDate!;

    const defaultTimecard: ITimecard = {
      id: guid(),
      earnedDate,
      employee,
      template,
      job: job?.effective.inRange(earnedDate) ? job : undefined,
      shift: idx,
      reason: template?.template?.[0].reason,
      group: job?.group,
      occupation: job?.occupation,
    };
    return (this.innerShifts[idx] = { job, template, details, defaultTimecard, hasChanges: false, isNew });
  }

  canCreateInnerShift(): boolean {
    return !this.viewOnly;
  }

  cloneTimecardWithNewId(timecard: ITimecard): ITimecard {
    const d = JSON.parse(JSON.stringify(timecard)) as ITimecard;
    d.earnedDate = new UTC(d.earnedDate);
    if (d.effective) {
      d.effective = new TimestampRange(d.effective);
    }
    d.employee = undefined;
    d.id = guid();
    return d;
  }

  createInnerShift(): boolean {
    const employee = scheduleModel.viewMode === 'Job' ? undefined : this.employee;
    const shift = this.innerShifts[this.currentInnerShiftIdx];
    if (shift && shift.details.length > 0) {
      const details = shift.details.map((d) => this.cloneTimecardWithNewId(d));
      this.addInnerShift(this.innerShifts.length, details, employee, this.job, details[0].template, true);
    } else {
      this.addInnerShift(this.innerShifts.length, [], employee, this.job, undefined, true);
    }
    infoPanelShiftViewModel.currentInnerShiftIdx = this.innerShifts.length - 1;
    shift.hasChanges = true;
    return false;
  }

  canRemoveInnerShift = () => {
    return !this.viewOnly && this.innerShifts.length > 0;
  };

  removeInnerShift = () => {
    if (this.innerShifts.length <= 1) {
      this.innerShifts[0].hasChanges = true;
      return;
    }
    this.innerShifts.splice(this.currentInnerShiftIdx, 1);
    // eslint-disable-next-line no-self-assign
    this.currentInnerShiftIdx = this.currentInnerShiftIdx;
    this.innerShifts[0].hasChanges = true;
  };

  resetShiftToDefault() {
    if (this.viewOnly || scheduleModel.viewMode === 'Employee') {
      return;
    }

    const jobInfo = scheduleModel.getJobInfo(scheduleInfoPanelModel.selectedCells);
    scheduleModel.confirmResetShiftsToDefault(jobInfo);
  }

  showDuplicateShiftDialog() {
    if (this.viewOnly || !this.editing || scheduleModel.viewMode === 'Employee') {
      return;
    }

    const duplicateShiftData = [] as IDuplicateShiftData[];
    for (const shift of scheduleModel.getShifts()) {
      if (!shift || !shift.details?.length) return;
      duplicateShiftData.push({
        jobId: shift.details[0].job!.id,
        earnedDate: shift.earnedDate,
        employee: undefined,
        reason: undefined,
        details: shift.details,
      } as IDuplicateShiftData);
    }

    duplicateMultipleShiftsDialogModel.load(duplicateShiftData);
  }

  addComment() {
    if (this.viewOnly) {
      return;
    }

    const comments = [] as MessageInput[];
    if (scheduleInfoPanelModel.selectedCells) {
      for (const cell of scheduleInfoPanelModel.selectedCells) {
        const job = scheduleModel.getJobFromCell(cell);
        const earnedDate = scheduleModel.getShiftCellEarnedDate(cell);
        const employeeId = scheduleModel.getEmployeeFromCell(cell)?.id ?? job?.employee?.id;
        const jobId = job.id;
        const groupId = job.group?.id;
        const comment = {
          level: 'info',
          jobId: jobId,
          groupId: groupId,
          employeeId: employeeId,
          effective: earnedDate,
          type: 'user',
        } as MessageInput;
        comments.push(comment);
      }
    }

    if (comments?.length) {
      commentDialogModel.load(comments);
    }
  }

  viewAuditLog() {
    const { minDate, maxDate } = getMinMaxDates(
      LQ(scheduleModel.getShifts())
        .map((s) => s.earnedDate)
        .toArray(),
    );

    const filter = { types: ['timecard'], effective: new DateRange(minDate, maxDate) } as IAuditLogFilters;
    const entityDataFilters = [];
    if (scheduleModel.viewMode === 'Job') {
      const jobIds = LQ(scheduleModel.getShifts())
        .map((s) => s.details[0].job!.id)
        .distinct()
        .toArray();
      if (jobIds.length) {
        entityDataFilters.push({ field: 'jobId', in: jobIds });
      }
    } else if (scheduleModel.viewMode === 'Employee') {
      const employeeIds = LQ(scheduleModel.getShifts())
        .map((s) => s.details[0].employee!.id)
        .distinct()
        .toArray();
      if (employeeIds.length) {
        entityDataFilters.push({ field: 'employeeId', in: employeeIds });
      }
    }

    auditLogDialogModel.load(filter, entityDataFilters, scheduleDisabledAuditLogColumns);
  }

  deleteShifts() {
    if (this.viewOnly || !this.editing) {
      return;
    }
    if (scheduleModel.viewMode === 'Job') {
      const jobInfo = scheduleModel.getJobInfo(scheduleInfoPanelModel.selectedCells);
      scheduleModel.confirmDeleteJobModeShifts(jobInfo);
    } else {
      const employeeModeJobInfo = scheduleModel.getEmployeeModeJobInfo(scheduleInfoPanelModel.selectedCells);
      scheduleModel.confirmDeleteEmployeeModeShifts(employeeModeJobInfo);
    }
  }

  undoAbsence() {
    if (this.viewOnly || this.isNew) {
      return;
    }

    const jobInfo = scheduleModel.getJobInfo(scheduleInfoPanelModel.selectedCells);
    scheduleModel.confirmUndoAbsences(jobInfo);
  }

  isView(view: IShiftViewType): boolean {
    return this.state.view === view;
  }

  setView = async (view: IShiftViewType) => {
    this.state.isLoading = true;
    this.state.view = view;
    await this.loadView();
    this.state.isLoading = false;
  };

  toDisplayDateString(utc?: UTC): string {
    if (!utc) return '';

    const d = new Date(utc.utc);
    const year = d.getUTCFullYear();
    const month = d.toLocaleDateString('en-us', { month: 'long', timeZone: 'UTC' });
    const day = d.getUTCDate();
    return `${day} ${month} ${year}`;
  }

  submit = async () => {
    return scheduleInfoPanelModel.submit();
  };
}

const infoPanelShiftViewModel = new InfoPanelShiftViewModel();
export default infoPanelShiftViewModel;
